import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { uploadVideo } from '../graphqlService';
import { uploadAssetToR2 } from '../services/r2AssetUpload';
import { detectFrameRate } from '../utils/detectFrameRate';
import { MAX_UPLOAD_FILE_BYTES, VIDEO_FILE_LIMIT_COPY, VIDEO_TOO_LARGE_COPY } from '../config/uploadConfig';
import { formatBytes } from '../utils/format';
import PageContainer from './ui/PageContainer';
import PageHeader from './ui/PageHeader';
import VideoDropZone, { UploadFileRow } from './Library/VideoDropZone';
import { MulticamUploadTip } from './Multicam/MulticamHowItWorks';
import { friendlyErrorMessage } from './Library/friendlyError';
import type { LibraryVideo } from './Library/libraryFormat';

/** Formats every current browser can decode. */
const RECOMMENDED_EXTENSIONS = ['mp4', 'm4v', 'mov', 'webm'];

const fileExtension = (fileName: string): string | null => {
  const match = /\.([a-z0-9]{1,8})$/i.exec(fileName);
  return match ? match[1].toLowerCase() : null;
};

/**
 * What to tell the user when the browser can't read a video's metadata.
 * The duration (and so the upload) comes from the browser decoding the
 * file, and AVI, MKV, WMV and similar containers usually can't be decoded;
 * a bare "Error loading video file" left people retrying the same file.
 */
export const unreadableVideoMessage = (fileName: string): string => {
  const extension = fileExtension(fileName);
  if (extension && !RECOMMENDED_EXTENSIONS.includes(extension)) {
    return `Your browser can't play .${extension} files, so PithPlay can't read this video. Convert it to MP4 (H.264) and upload it again.`;
  }
  return "PithPlay can't read this video. It may be damaged or use a codec your browser can't play. Convert it to MP4 (H.264) and upload it again.";
};

/** Under the drop zone: formats, the plan limits, and what comes next. */
export const UPLOAD_HINT = `MP4, MOV or WebM. ${VIDEO_FILE_LIMIT_COPY}. You'll annotate it next.`;

const NOT_A_VIDEO = "That file isn't a video. Choose an MP4, MOV or WebM file.";
const ONE_AT_A_TIME = 'Drop one video at a time.';
const UPLOAD_CANCELLED = 'Upload cancelled.';
const UPLOAD_FAILED = "The upload didn't finish. Check your connection and try again.";

/**
 * The reason an upload failed, in words a person can act on. The API's
 * refusals ("Files can be up to 1 GB on your plan…", storage limits) are
 * shown as they are; network and storage internals get UPLOAD_FAILED.
 */
export const uploadErrorMessage = (error: unknown): string => {
  const message = friendlyErrorMessage(error, UPLOAD_FAILED);
  if (/invalid content type/i.test(message)) {
    return "PithPlay can't take this type of file. Upload an MP4, MOV or WebM video.";
  }
  return message;
};

/** Reading a file's metadata is local and quick; past this, treat it as unreadable rather than wait forever. */
const METADATA_TIMEOUT_MS = 60_000;

type Video = LibraryVideo;

interface UploadVideoProps {
  /** The Library adds the new video to its grid; without this, the upload opens the annotation studio. */
  onVideoUploaded?: (newVideo: Video) => void;
  /** Render only the drop zone, inside another page. Without it this is the /upload page. */
  embedded?: boolean;
  /** Told when an upload starts and stops, so a host can keep its panel open meanwhile. */
  onUploadingChange?: (uploading: boolean) => void;
  /** Lets a host move focus to the drop zone. */
  dropZoneRef?: React.MutableRefObject<HTMLDivElement | null>;
}

const UploadVideo: React.FC<UploadVideoProps> = ({ onVideoUploaded, embedded = false, onUploadingChange, dropZoneRef }) => {
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  // Neutral information, e.g. a cancelled upload: not an error.
  const [notice, setNotice] = useState<string | null>(null);
  // The video the last upload created, for the "Annotate" action.
  const [uploaded, setUploaded] = useState<{ id: string; url: string; name: string } | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [activeUploadTask, setActiveUploadTask] = useState<AbortController | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  const showError = (message: string) => {
    setError(message);
    setNotice(null);
    setUploaded(null);
  };

  const showNotice = (message: string) => {
    setNotice(message);
    setError(null);
    setUploaded(null);
  };

  const clearMessages = () => {
    setError(null);
    setNotice(null);
    setUploaded(null);
  };

  // Lets the same file be chosen again.
  const resetInput = () => {
    if (inputRef.current) inputRef.current.value = '';
  };

  const uploadingChangeRef = useRef(onUploadingChange);
  uploadingChangeRef.current = onUploadingChange;
  useEffect(() => {
    uploadingChangeRef.current?.(uploading);
  }, [uploading]);
  useEffect(() => () => uploadingChangeRef.current?.(false), []);

  // Closing or reloading the tab would abandon a transfer that may have run
  // for many minutes; the browser asks first.
  useEffect(() => {
    if (!uploading) return undefined;
    const warnBeforeLeaving = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeLeaving);
    return () => window.removeEventListener('beforeunload', warnBeforeLeaving);
  }, [uploading]);

  const isE2EUploadMockEnabled = (() => {
    if (process.env.NODE_ENV === 'production') return false;
    if (process.env.REACT_APP_E2E_AUTH_BYPASS !== '1') return false;
    try {
      return window.localStorage.getItem('pithplay_e2e_auth_bypass') === '1';
    } catch {
      return false;
    }
  })();

  const isLikelyVideoFile = (selectedFile: File) => {
    if (selectedFile.type?.startsWith('video/')) return true;
    return /\.(mp4|mov|avi|mkv|webm|m4v)$/i.test(selectedFile.name);
  };

  const processFile = (selectedFile: File | undefined) => {
    // A cancelled file picker can report a change with no file.
    if (!selectedFile || uploading) return;

    if (!isLikelyVideoFile(selectedFile)) {
      showError(NOT_A_VIDEO);
      setFile(null);
      setUploading(false);
      resetInput();
      return;
    }

    // The absolute ceiling (25 GB). The API refuses anything over the plan's
    // own limit (1 GB on Free, 10 GB on Pro) before uploading, naming it.
    if (selectedFile.size > MAX_UPLOAD_FILE_BYTES) {
      showError(`This file is ${formatBytes(selectedFile.size)}. ${VIDEO_TOO_LARGE_COPY}`);
      setFile(null);
      setUploading(false);
      resetInput();
      return;
    }

    setFile(selectedFile);
    clearMessages();
    // Start straight away; there is nothing else to fill in.
    handleUpload(selectedFile);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    processFile(e.target.files?.[0]);
  };

  const handleDropFiles = (files: FileList) => {
    if (uploading) return;
    if (files.length > 1) {
      showError(ONE_AT_A_TIME);
      return;
    }
    processFile(files[0]);
  };

  const finishSuccessfulUpload = (created: { id: string; url: string; name: string; size: number; duration: string }) => {
    setNotice(null);
    setUploaded({ id: created.id, url: created.url, name: created.name });
    setUploading(false);
    setActiveUploadTask(null);

    if (onVideoUploaded) {
      onVideoUploaded({
        id: created.id,
        url: created.url,
        name: created.name,
        size: created.size,
        duration: created.duration,
      });
    } else {
      navigate(`/annotate/${created.id}`, {
        state: { videoUrl: created.url, videoName: created.name },
      });
    }

    setFile(null);
    resetInput();
  };

  const handleUpload = async (selectedFile: File) => {
    if (!selectedFile) return;
    if (!user) {
      showError('Sign in to upload a video.');
      setUploading(false);
      return;
    }

    if (isE2EUploadMockEnabled) {
      (async () => {
        try {
          setUploading(true);
          setProgress(0);
          clearMessages();
          setActiveUploadTask(null);

          await new Promise((r) => setTimeout(r, 150));
          setProgress(35);

          const downloadURL = URL.createObjectURL(selectedFile);

          const token = await user.getIdToken(true);
          const duration = '0:00';
          const uploadedVideo = await uploadVideo(
            downloadURL,
            selectedFile.name,
            selectedFile.size,
            duration,
            token
          );

          if (!uploadedVideo?.id) {
            throw new Error('Upload completed but server did not return a video id');
          }

          setProgress(100);
          finishSuccessfulUpload({
            id: uploadedVideo.id,
            url: downloadURL,
            name: selectedFile.name,
            size: selectedFile.size,
            duration,
          });
        } catch (err: any) {
          showError(uploadErrorMessage(err));
          setUploading(false);
          setProgress(0);
        }
      })();
      return;
    }

    setUploading(true);
    setProgress(0);
    clearMessages();

    // Extract duration from the local file before uploading so we can fail fast on corrupt/invalid files
    // and avoid leaving orphaned uploads in storage.
    let duration = '0:00';
    try {
      duration = await getVideoDuration(selectedFile);
    } catch (err: any) {
      showError(err?.message || unreadableVideoMessage(selectedFile.name));
      setUploading(false);
      setProgress(0);
      setActiveUploadTask(null);
      setFile(null);
      resetInput();
      return;
    }

    const controller = new AbortController();
    setActiveUploadTask(controller);
    try {
      const token = await user.getIdToken(true);
      // Measure the frame rate alongside the transfer so it adds no wait. It
      // is what lets exported markers land on the right frame.
      const frameRatePromise = detectFrameRate(selectedFile).catch(() => null);

      const stored = await uploadAssetToR2(
        selectedFile,
        'original-video',
        token,
        setProgress,
        controller.signal,
        {
          // Large files resume where they stopped when the same file is chosen again.
          onResume: ({ uploadedBytes, totalBytes }) =>
            showNotice(`Continuing your earlier upload of this file. ${formatBytes(uploadedBytes)} of ${formatBytes(totalBytes)} was already uploaded.`),
        },
      );
      // The abort signal only covers the R2 PUT. If the user cancelled while
      // that request was finishing, stop here instead of registering the video
      // and navigating away after the UI already said "Upload cancelled".
      if (controller.signal.aborted) {
        return;
      }

      // The token above was captured before the upload. Firebase tokens last an
      // hour and a large file can take longer, so get a current one (cached
      // unless it has expired) before registering the video.
      const freshToken = await user.getIdToken();
      const uploadedVideo = await uploadVideo(
        stored.url,
        selectedFile.name,
        selectedFile.size,
        duration,
        freshToken,
        { storageProvider: stored.storageProvider, storageKey: stored.objectKey },
        // Measured while the file was uploading; null means unknown, never a guess.
        await frameRatePromise,
      );
      if (!uploadedVideo?.id) {
        throw new Error('Upload completed but server did not return a video id');
      }
      if (controller.signal.aborted) {
        return;
      }
      finishSuccessfulUpload({
        id: uploadedVideo.id,
        url: stored.url,
        name: selectedFile.name,
        size: selectedFile.size,
        duration,
      });
    } catch (uploadError: any) {
      if (uploadError?.name === 'AbortError') {
        showNotice(UPLOAD_CANCELLED);
      } else {
        showError(uploadErrorMessage(uploadError));
      }
      setUploading(false);
      setProgress(0);
      setActiveUploadTask(null);
      setFile(null);
      resetInput();
    }
  };

  const handleCancelUpload = () => {
    if (!activeUploadTask) return;

    try {
      activeUploadTask.abort();
    } catch (error: any) {
      console.warn('Failed to cancel upload:', error);
    } finally {
      setActiveUploadTask(null);
      setUploading(false);
      setProgress(0);
      setFile(null);
      showNotice(UPLOAD_CANCELLED);
      resetInput();
    }
  };

  const getVideoDuration = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      const timeout = window.setTimeout(() => {
        window.URL.revokeObjectURL(video.src);
        reject(new Error(unreadableVideoMessage(file.name)));
      }, METADATA_TIMEOUT_MS);
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        window.clearTimeout(timeout);
        window.URL.revokeObjectURL(video.src);
        const duration = video.duration;
        // Some recordings (often WebM) report no length until played through;
        // store "unknown" rather than "Infinity:NaN".
        if (!Number.isFinite(duration) || duration < 0) {
          resolve('0:00');
          return;
        }
        const minutes = Math.floor(duration / 60);
        const seconds = Math.floor(duration % 60);
        resolve(`${minutes}:${seconds.toString().padStart(2, '0')}`);
      };
      video.onerror = () => {
        window.clearTimeout(timeout);
        window.URL.revokeObjectURL(video.src);
        reject(new Error(unreadableVideoMessage(file.name)));
      };
      video.src = URL.createObjectURL(file);
    });
  };

  const uploader = (
    <Box>
      <VideoDropZone
        inputRef={inputRef}
        zoneRef={dropZoneRef}
        onInputChange={handleFileChange}
        onDropFiles={handleDropFiles}
        disabled={uploading}
        emphasis={embedded ? 'secondary' : 'primary'}
        hint={UPLOAD_HINT}
      />

      <MulticamUploadTip />

      {file && uploading && (
        <UploadFileRow
          name={file.name}
          size={file.size}
          progress={progress}
          onCancel={activeUploadTask ? handleCancelUpload : undefined}
        />
      )}

      {uploaded && (
        <Alert
          severity="success"
          sx={{ mt: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              component={RouterLink}
              to={`/annotate/${uploaded.id}`}
              state={{ videoUrl: uploaded.url, videoName: uploaded.name }}
            >
              Annotate
            </Button>
          }
        >
          {embedded ? 'Video uploaded. It now appears in your library below.' : 'Video uploaded. Opening it for annotation…'}
        </Alert>
      )}

      {notice && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {notice}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </Box>
  );

  if (embedded) return uploader;

  return (
    <PageContainer width="narrow">
      <PageHeader
        title="Upload video"
        subtitle="Add the footage you want edited to your library."
        backTo={{ to: '/library', label: 'Library' }}
      />
      {uploader}
    </PageContainer>
  );
};

export default UploadVideo;
